'use client';
/** @jsxImportSource @emotion/react */

import { forwardRef } from 'react';

import { buttonBaseClasses } from '@mui/material/ButtonBase';
import MuiCheckbox, {
  type CheckboxProps as MuiCheckboxProps,
} from '@mui/material/Checkbox';
import { useTheme } from '@mui/material/styles';

declare module '@mui/material/Checkbox' {
  interface CheckboxPropsColorOverrides {
    forceWhite: true;
  }
}

type ExcludedMuiProps =
  | 'checkedIcon'
  /**
   * The Checkbox component uses a <span> as its root element, which is essential for its styling and interactive behavior.
   * Changing the root element could disrupt its functionality, such as click handling and accessibility features.
   * Keeping the default element ensures proper behavior and layout.
   * And the default implementation in MUI is also broken, and altering it could lead to issues.
   *
   * https://serko.atlassian.net/browse/ZDS-1033
   */
  | 'component'
  | 'disableFocusRipple'
  | 'disableRipple'
  | 'disableTouchRipple'
  | 'icon'
  | 'indeterminateIcon';

type CheckboxProps = Omit<MuiCheckboxProps, ExcludedMuiProps>;

export type { CheckboxProps };

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ color, sx = [], ...props }, ref) => {
    const theme = useTheme();
    const resolvedColor =
      color === 'forceWhite' ? theme.palette.forceWhite.main : color;

    return (
      <MuiCheckbox
        {...props}
        color={color}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          {
            [`&.Mui-focusVisible::after`]: {
              borderRadius: `${theme.ui.focus.borderRadius}`,
              outline: `${theme.ui.focus.outlineWidth} solid ${color === 'forceWhite' ? theme.palette.forceWhite.main : theme.palette.action.focusOutline}`,
            },
            [` &.${buttonBaseClasses.root}`]: {
              color: !props.disabled && resolvedColor,
            },
          },
        ]}
        ref={ref}
      />
    );
  }
);

Checkbox.displayName = 'Checkbox';
